import {
  ChangeEvent,
  FC,
  FocusEvent,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  AsYouType,
  CountryCode,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js'
import styled, { css } from 'styled-components'

import SuccessIcon from 'src/Assets/Svg/pages-icons/Success.svg?react'

import { PrimaryInput, PrimaryInputProps } from '../PrimaryInput'
import { CountryFlag } from '../CountryFlag'

const INVALID_PHONE_NUMBER_MESSAGE =
  'Phone number is invalid. Please enter a correct one.'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const FlagWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;

  ${({ $disabled }) =>
    $disabled &&
    css`
      filter: grayscale(0.5);
      opacity: 0.5;
    `}
`

const StyledPrimaryPhoneInput = styled(PrimaryInput)<{
  $hasValue?: boolean
  $hasError?: boolean
}>`
  &&:has(input:not([value=''])) {
    input {
      padding-top: 0px !important;
      padding-bottom: 0px !important;
    }
  }

  ${({ theme, $hasError }) =>
    $hasError && `border: 1px solid ${theme.color.mediumRed} !important;`}

  && {
    padding-bottom: ${({ $hasValue }) =>
      $hasValue ? '4px' : '16px'} !important;
  }

  &:not([value='']) {
    padding-top: 16px !important;
  }

  &.ant-input-affix-wrapper-focused {
    padding-bottom: 4px !important;
    border: ${({ theme, $hasError }) => {
      if ($hasError) {
        return `1px solid ${theme.color.mediumRed}`
      }
      return `1px solid ${theme.color.brandGreen}`
    }} !important;
  }
  height: 56px;
  input {
    &::placeholder {
      color: ${({ theme }) => theme.color.slateGrey};
    }
  }

  &.ant-input-affix-wrapper-disabled {
    color: ${({ theme }) => theme.color.lightGrey};
  }
`

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  color: ${({ theme }) => theme.color.brandGreen};
`

const IconWrapper = styled.div`
  margin-top: 3px;
`

const MAX_LENGTH = 20

export type PrimaryPhoneInputProps = {
  country?: CountryCode
  onValidationError?: (error: string | null) => void
} & PrimaryInputProps

export const PrimaryPhoneInput: FC<PrimaryPhoneInputProps> = ({
  country = 'US',
  placeholder = 'Phone',
  value,
  onChange,
  onFocus,
  onBlur,
  onValidationError,
  disabled,
  autoComplete,
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [hasBlurred, setHasBlurred] = useState(false)

  const formattedValue = useMemo(() => {
    if (!value || typeof value !== 'string') {
      return ''
    }

    const formatter = new AsYouType(country)
    return formatter.input(value.toString())
  }, [value, country])

  const validationError = useMemo(() => {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return null
    }

    const phoneString = value.toString()
    const lengthValidation = validatePhoneNumberLength(phoneString, country)

    if (lengthValidation === 'TOO_SHORT') {
      if (!hasBlurred) {
        return null
      }
      return INVALID_PHONE_NUMBER_MESSAGE
    }

    const isPossible = isPossiblePhoneNumber(phoneString, country)
    const isValid = isValidPhoneNumber(phoneString, country)

    if (!isPossible || !isValid) {
      return INVALID_PHONE_NUMBER_MESSAGE
    }

    return null
  }, [value, country, hasBlurred])

  const isSuccessMessage = useMemo(() => {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return null
    }

    const phoneString = value.toString()
    const isPossible = isPossiblePhoneNumber(phoneString, country)
    const isValid = isValidPhoneNumber(phoneString, country)

    return Boolean(isPossible && isValid)
  }, [value, country])

  useEffect(() => {
    onValidationError?.(validationError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationError])

  useEffect(() => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      setHasBlurred(false)
    }
  }, [value])

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target) {
      return
    }
    const inputValue = e.target.value
    const formatter = new AsYouType(country)
    formatter.input(inputValue)

    const phoneNumber = formatter.getNumber()
    const rawNumber = phoneNumber ? phoneNumber.number : inputValue

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: rawNumber,
      },
    } as ChangeEvent<HTMLInputElement>

    onChange?.(syntheticEvent)
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true)
    onFocus?.(e)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false)
    setHasBlurred(true)
    onBlur?.(e)
  }

  const showFlag = isFocused || Boolean(formattedValue)
  const prefix = showFlag ? (
    <FlagWrapper $disabled={disabled}>
      <CountryFlag width={16} height={10} country={country} />
    </FlagWrapper>
  ) : (
    <span />
  )

  return (
    <Container>
      <StyledPrimaryPhoneInput
        $hasValue={Boolean(formattedValue)}
        $hasError={Boolean(validationError)}
        prefix={prefix}
        placeholder={placeholder}
        value={formattedValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        error={validationError}
        disabled={disabled}
        maxLength={MAX_LENGTH}
        autoComplete={autoComplete}
      />
      {isSuccessMessage && (
        <SuccessContainer>
          <IconWrapper>
            <SuccessIcon width={16} height={16} />
          </IconWrapper>

          <span>Great, you can get a quick review response via SMS</span>
        </SuccessContainer>
      )}
    </Container>
  )
}
